// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import {
  ErrorBoundary,
  type FallbackProps,
  useErrorBoundary,
} from 'react-error-boundary';
import ReactGA from 'react-ga4';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { type PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import {
  addNotification,
  closeBudget,
  loadBudget,
  loadGlobalPrefs,
  setAppState,
  signOut,
} from 'loot-core/client/actions';
import { listen } from 'loot-core/platform/client/fetch/index.browser';
import * as Platform from 'loot-core/src/client/platform';
import { type State } from 'loot-core/src/client/state-types';
import {
  init as initConnection,
  send,
} from 'loot-core/src/platform/client/fetch';

import { APP_CONFIG } from '../app-config';
import { useAuth } from '../hooks/useAuth';
import { useLocalPref } from '../hooks/useLocalPref';
import { useMigrator } from '../hooks/useMigrator';
import { useSync } from '../hooks/useSync';
import { installPolyfills } from '../polyfills';
import { ResponsiveProvider } from '../ResponsiveProvider';
import { hasHiddenScrollbars, styles, ThemeStyle } from '../style';

import { AppBackground } from './AppBackground';
import { AuthProvider } from './AuthContext';
import { View } from './common/View';
import { DevelopmentTopBar } from './DevelopmentTopBar';
import { FatalError } from './FatalError';
import { FinancesApp } from './FinancesApp';
import { ManagementApp } from './manager/ManagementApp';
import { MobileWebMessage } from './mobile/MobileWebMessage';
import { PageTitleProvider } from './PageTitleContext';
import { UpdateNotification } from './UpdateNotification';
import { GoogleOAuthProvider } from '@react-oauth/google';

const posthogOptions: Partial<PostHogConfig> = {
  api_host: 'https://ph.goodsteward.io',
  ui_host: 'https://us.posthog.com',
};

type AppInnerProps = {
  budgetId: string;
  cloudFileId: string;
};

function AppInner({ budgetId, cloudFileId }: AppInnerProps) {
  useMigrator();

  const [ initializing, setInitializing ] = useState(true);
  const { hasRemoteAccount, refreshAuthState, getUser } = useAuth();
  const { showBoundary: showErrorBoundary } = useErrorBoundary();
  const loadingText = useSelector((state: State) => state.app.loadingText);
  const dispatch = useDispatch();

  async function init() {
    const socketName = await global.Actual.getServerSocket();

    // TODO: Should this run on mobile too?
    listen('sync-event', async ({ type }) => {
      if (type === 'unauthorized' && hasRemoteAccount) {
        dispatch(
          addNotification({
            type: 'warning',
            message:
              'You are logged out, please sign-in to synchronize your budget data',
            sticky: true,
            id: 'auth-issue',
          }),
        );
        await dispatch(signOut());
        await refreshAuthState();
      }
    });

    // this is triggered when any of the HTTP calls fails because of 401
    listen('unauthorized', async () => {
      await dispatch(signOut());
      await refreshAuthState();
    });

    dispatch(
      setAppState({
        loadingText: 'Initializing the connection to the local database...',
      }),
    );
    await initConnection(socketName);
    ReactGA.initialize(APP_CONFIG.googleAnalyticsId);

    // Load any global prefs
    dispatch(
      setAppState({
        loadingText: 'Loading global preferences...',
      }),
    );
    await dispatch(loadGlobalPrefs());

    await refreshAuthState();

    // Open the last opened budget, if any
    dispatch(
      setAppState({
        loadingText: 'Opening last budget...',
      }),
    );
    const budgetId = await send('get-last-opened-backup');
    if (budgetId) {
      await dispatch(loadBudget(budgetId, 'Loading the last budget file...'));

      // Check to see if this file has been remotely deleted (but
      // don't block on this in case they are offline or something)
      dispatch(
        setAppState({
          loadingText: 'Retrieving remote files...',
        }),
      );
      send('get-remote-files').then(files => {
        if (files) {
          const remoteFile = files.find(f => f.fileId === cloudFileId);
          if (remoteFile && remoteFile.deleted) {
            dispatch(closeBudget());
          }
        }
      });
    }
  }

  useEffect(() => {
    async function initAll() {
      await Promise.all([installPolyfills(), init()]);
      setInitializing(false);
      dispatch(
        setAppState({
          loadingText: null,
        }),
      );
    }

    initAll().catch(showErrorBoundary);
  }, []);

  useEffect(() => {
    global.Actual.updateAppMenu(budgetId);
  }, [budgetId]);

  const chatwoot = (window as any).$chatwoot;
  useEffect(() => {
    async function setUser() {
      if (!chatwoot) {
        return;
      }

      const user = await getUser();
      chatwoot.setUser(user.id, {
        email: user.email,
        name: user.name,
      });
    }

    setUser();
  }, [!!chatwoot]);

  return (
    <>
      <GoogleOAuthProvider clientId={APP_CONFIG.googleClientId}>
        <PageTitleProvider>
          {(initializing || !budgetId) && (
            <AppBackground
              initializing={initializing}
              loadingText={loadingText}
            />
          )}
          {!initializing &&
            (budgetId ? (
              <FinancesApp />
            ) : (
              <ManagementApp isLoading={loadingText != null} />
            ))}
        </PageTitleProvider>

        <UpdateNotification />
        <MobileWebMessage />
      </GoogleOAuthProvider>
    </>
  );
}

function ErrorFallback({ error }: FallbackProps) {
  return (
    <>
      <AppBackground />
      <FatalError error={error} />
    </>
  );
}

export function App() {
  const [budgetId] = useLocalPref('id');
  const [cloudFileId] = useLocalPref('cloudFileId');
  const [hiddenScrollbars, setHiddenScrollbars] = useState(
    hasHiddenScrollbars(),
  );
  const { smartSync } = useSync();
  const dispatch = useDispatch();

  useEffect(() => {
    function checkScrollbars() {
      if (hiddenScrollbars !== hasHiddenScrollbars()) {
        setHiddenScrollbars(hasHiddenScrollbars());
      }
    }

    let isSyncing = false;

    // TODO: why is this in App.tsx and not in FinancesApp.tsx?
    async function onVisibilityChange() {
      if (!isSyncing) {
        isSyncing = true;

        await smartSync();

        isSyncing = false;
      }
    }

    window.addEventListener('focus', checkScrollbars);
    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', checkScrollbars);
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch]);

  return (
    <AuthProvider>
      <HotkeysProvider initiallyActiveScopes={['*']}>
        <ResponsiveProvider>
          <PostHogProvider
            apiKey={(window as any).POSTHOG_API_KEY}
            options={posthogOptions}
          >
            <View
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <View
                key={hiddenScrollbars ? 'hidden-scrollbars' : 'scrollbars'}
                style={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  ...styles.lightScrollbar,
                }}
              >
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {process.env.REACT_APP_REVIEW_ID &&
                    !Platform.isPlaywright && <DevelopmentTopBar />}
                  <AppInner budgetId={budgetId} cloudFileId={cloudFileId} />
                </ErrorBoundary>

                <ThemeStyle />
              </View>
            </View>
          </PostHogProvider>
        </ResponsiveProvider>
      </HotkeysProvider>
    </AuthProvider>
  );
}
